import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useSettings } from "@/hooks/useSettings";
import { TESTIDS } from "@/constants/testIds";
import { clearAll } from "@/lib/storage";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

function Row({ label, hint, control }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-border/60 last:border-0">
      <div className="flex-1">
        <p className="font-medium">{label}</p>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}

export function Settings() {
  const { settings, update, reset } = useSettings();

  return (
    <Layout>
      <section
        data-testid={TESTIDS.settingsPage}
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
      >
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Preferences</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-1">Settings</h1>

        <Card className="mt-8 p-5">
          <p className="font-display text-xl mb-1">Game</p>
          <Row
            label="Sound effects"
            hint="Subtle clicks for pit selection, sowing, captures."
            control={
              <Switch
                data-testid={TESTIDS.settingSound}
                checked={settings.sound}
                onCheckedChange={(v) => update({ sound: v })}
              />
            }
          />
          <Row
            label="Background music"
            hint="Off by default. Will not autoplay loudly."
            control={
              <Switch
                data-testid={TESTIDS.settingMusic}
                checked={settings.music}
                onCheckedChange={(v) => update({ music: v })}
              />
            }
          />
          <Row
            label="Haptic feedback"
            hint="On supported mobile devices."
            control={
              <Switch
                data-testid={TESTIDS.settingHaptics}
                checked={settings.haptics}
                onCheckedChange={(v) => update({ haptics: v })}
              />
            }
          />
          <Row
            label="Animation speed"
            control={
              <div className="w-40">
                <Slider
                  data-testid={TESTIDS.settingAnimationSpeed}
                  min={0.5}
                  max={2}
                  step={0.1}
                  value={[settings.animationSpeed]}
                  onValueChange={([v]) => update({ animationSpeed: v })}
                />
                <p className="text-[10px] font-mono text-muted-foreground mt-1 text-right">
                  {settings.animationSpeed.toFixed(1)}×
                </p>
              </div>
            }
          />
          <Row
            label="Reduced motion"
            hint="Replace animations with simple fades."
            control={
              <Switch
                data-testid={TESTIDS.settingReducedMotion}
                checked={settings.reducedMotion}
                onCheckedChange={(v) => update({ reducedMotion: v })}
              />
            }
          />
          <Row
            label="Confirm each move"
            hint="Tap to preview, tap again to confirm."
            control={
              <Switch
                data-testid={TESTIDS.settingConfirmMove}
                checked={settings.confirmMove}
                onCheckedChange={(v) => update({ confirmMove: v })}
              />
            }
          />
          <Row
            label="Show legal-move hints"
            control={
              <Switch
                data-testid={TESTIDS.settingHints}
                checked={settings.hints}
                onCheckedChange={(v) => update({ hints: v })}
              />
            }
          />
          <Row
            label="Plain-language move explanations"
            control={
              <Switch
                checked={settings.moveExplanations}
                onCheckedChange={(v) => update({ moveExplanations: v })}
              />
            }
          />
        </Card>

        <Card className="mt-6 p-5">
          <p className="font-display text-xl mb-1">Accessibility</p>
          <Row
            label="High contrast"
            control={
              <Switch
                data-testid={TESTIDS.settingHighContrast}
                checked={settings.highContrast}
                onCheckedChange={(v) => update({ highContrast: v })}
              />
            }
          />
          <Row
            label="Text size"
            control={
              <div className="w-32">
                <Select value={settings.textSize} onValueChange={(v) => update({ textSize: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            }
          />
          <Row
            label="Show row/column coordinates"
            control={
              <Switch
                data-testid={TESTIDS.settingCoords}
                checked={settings.coordLabels}
                onCheckedChange={(v) => update({ coordLabels: v })}
              />
            }
          />
          <Row
            label="Pass-device screen between turns"
            hint="Local 2-player only."
            control={
              <Switch
                data-testid={TESTIDS.settingPassDevice}
                checked={settings.passDeviceScreen}
                onCheckedChange={(v) => update({ passDeviceScreen: v })}
              />
            }
          />
          <Row
            label="Language"
            control={
              <Label className="text-muted-foreground text-sm">English</Label>
            }
          />
        </Card>

        <div className="mt-6 flex gap-2 flex-wrap">
          <Button variant="outline" onClick={reset} className="rounded-full">
            Reset settings
          </Button>
          <Button
            data-testid={TESTIDS.settingResetData}
            variant="destructive"
            className="rounded-full"
            onClick={() => {
              if (confirm("Erase all local Play Tsoro data (settings, stats, saved game)? This cannot be undone.")) {
                clearAll();
                toast.success("All local data erased.");
                setTimeout(() => window.location.reload(), 500);
              }
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Reset all local data
          </Button>
        </div>
      </section>
    </Layout>
  );
}
